import { create } from 'zustand';
import { persist, type StorageValue } from 'zustand/middleware';
import type { ThemeMode } from '@/styles/theme';

interface UIState {
  // Theme
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  toggleThemeMode: () => void;

  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  /** Nav groups the user has opened in the expanded sidebar; groups start closed (#3534). */
  expandedNavGroups: string[];
  toggleNavGroup: (groupId: string) => void;
  expandNavGroup: (groupId: string) => void;

  // Mobile
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;

  // Layout
  fullWidthLayout: boolean;
  toggleFullWidthLayout: () => void;

  // Notes dialog sizing
  notesDialogWidth: number | null;
  notesDialogHeight: number | null;
  notesInputHeight: number | null;
  setNotesDialogSize: (w: number, h: number) => void;
  setNotesInputHeight: (h: number) => void;
}

// Get browser's preferred color scheme
function getSystemTheme(): ThemeMode {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

type PersistedState = Pick<UIState, 'themeMode' | 'sidebarCollapsed' | 'expandedNavGroups' | 'fullWidthLayout' | 'notesDialogWidth' | 'notesDialogHeight' | 'notesInputHeight'>;

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Theme - defaults to system preference
      themeMode: getSystemTheme(),
      setThemeMode: (mode) => set({ themeMode: mode }),
      toggleThemeMode: () =>
        set((state) => ({
          themeMode: state.themeMode === 'light' ? 'dark' : 'light',
        })),

      // Sidebar
      sidebarOpen: true,
      sidebarCollapsed: false,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      expandedNavGroups: [],
      toggleNavGroup: (groupId) =>
        set((state) => ({
          expandedNavGroups: state.expandedNavGroups.includes(groupId)
            ? state.expandedNavGroups.filter((id) => id !== groupId)
            : [...state.expandedNavGroups, groupId],
        })),
      expandNavGroup: (groupId) =>
        set((state) =>
          state.expandedNavGroups.includes(groupId)
            ? state
            : { expandedNavGroups: [...state.expandedNavGroups, groupId] },
        ),

      // Layout
      fullWidthLayout: false,
      toggleFullWidthLayout: () =>
        set((state) => ({ fullWidthLayout: !state.fullWidthLayout })),

      // Mobile
      mobileMenuOpen: false,
      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),

      // Notes dialog sizing
      notesDialogWidth: null,
      notesDialogHeight: null,
      notesInputHeight: null,
      setNotesDialogSize: (w, h) => set({ notesDialogWidth: w, notesDialogHeight: h }),
      setNotesInputHeight: (h) => set({ notesInputHeight: h }),
    }),
    {
      name: 'staff-portal-ui',
      partialize: (state) => ({
        themeMode: state.themeMode,
        sidebarCollapsed: state.sidebarCollapsed,
        expandedNavGroups: state.expandedNavGroups,
        fullWidthLayout: state.fullWidthLayout,
        notesDialogWidth: state.notesDialogWidth,
        notesDialogHeight: state.notesDialogHeight,
        notesInputHeight: state.notesInputHeight,
      }),
      // Merge persisted state with defaults, using system theme if not persisted
      merge: (persisted, current) => {
        const persistedState = persisted as StorageValue<PersistedState>['state'] | undefined;
        return {
          ...current,
          ...persistedState,
          // Use system theme if themeMode wasn't persisted
          themeMode: persistedState?.themeMode ?? getSystemTheme(),
        };
      },
    }
  )
);
